/**
 * Theme Editor Integration for Animation System
 *
 * ISSUE:
 * Sections with animations appear blank in Shopify theme editor after updates.
 *
 * ROOT CAUSE:
 * - Our animation system uses IntersectionObserver initialized on DOMContentLoaded
 * - Theme editor updates sections via AJAX without full page reload
 * - DOMContentLoaded already fired, won't fire again for new sections
 * - AnimationObserver never initializes → elements stuck at opacity: 0
 *
 * SOLUTION:
 * Listen for Shopify theme editor events and re-initialize animations:
 * - shopify:section:load → Re-init animations for reloaded section
 * - shopify:section:unload → Clean up observers to prevent memory leaks
 *
 * EDGE CASES HANDLED:
 * - AnimationObserver class not loaded yet → graceful fallback
 * - Already animated elements → reset before re-animating
 * - Memory leaks → cleanup existing observers before creating new
 * - Viewport detection → trigger animations immediately in theme editor
 * - Rapid section reloads → prevent duplicate observers
 * - Error handling → try-catch to prevent crashes
 *
 * REFERENCE:
 * https://shopify.dev/docs/storefronts/themes/architecture/sections/integrate-sections-with-the-theme-editor
 */

/**
 * Check if we're in Shopify theme editor (design mode)
 * @returns {boolean}
 */
function isInThemeEditor() {
  return window.Shopify && window.Shopify.designMode === true;
}

/**
 * Reset animation state for an element
 * Required because elements keep their animated state after first trigger
 * @param {Element} element - The element to reset
 */
function resetElementAnimation(element) {
  element.classList.remove('animate-in', 'animate-in-fast', 'animate-in-slow');
}

/**
 * Trigger animation immediately (for theme editor)
 * @param {Element} element - The element to animate
 */
function triggerImmediateAnimation(element) {
  // Reset first to ensure clean state
  resetElementAnimation(element);

  // Small delay to ensure CSS reset is applied
  requestAnimationFrame(() => {
    // Determine animation speed based on classes
    if (element.classList.contains('animate-fast')) {
      element.classList.add('animate-in-fast');
    } else if (element.classList.contains('animate-slow')) {
      element.classList.add('animate-in-slow');
    } else {
      element.classList.add('animate-in');
    }
  });
}

/**
 * Re-initialize animations for a specific section
 * @param {Element} sectionElement - The section DOM element
 */
function initializeSectionAnimations(sectionElement) {
  try {
    // Cleanup any existing observer first to prevent memory leaks
    if (sectionElement._axiomAnimator) {
      sectionElement._axiomAnimator.destroy();
      delete sectionElement._axiomAnimator;
    }

    // Query for all animated elements within this section only
    const animatedElements = sectionElement.querySelectorAll(`
      .fade-in-up, .fade-in-down, .fade-in-left, .fade-in-right,
      .fade-in, .scale-in, .reveal-on-scroll
    `);

    const staggeredElements = sectionElement.querySelectorAll(
      '.stagger-children, .stagger-children-left'
    );

    // Only initialize if there are animated elements
    if (animatedElements.length === 0 && staggeredElements.length === 0) {
      return;
    }

    // In theme editor: trigger animations immediately
    // In production: use IntersectionObserver for scroll animations
    if (isInThemeEditor()) {
      // Theme editor mode: Show animations immediately (no scroll required)

      // Animate regular elements
      animatedElements.forEach(element => {
        triggerImmediateAnimation(element);
      });

      // Animate staggered children
      staggeredElements.forEach(parent => {
        const children = parent.children;
        const staggerDelay = parseInt(parent.dataset.staggerDelay) || 100;

        Array.from(children).forEach((child, index) => {
          setTimeout(() => {
            triggerImmediateAnimation(child);
          }, index * staggerDelay);
        });
      });

    } else {
      // Production mode: Use IntersectionObserver (requires AnimationObserver class)

      // Check if AnimationObserver class exists
      if (typeof AnimationObserver === 'undefined') {
        console.warn('AnimationObserver class not loaded. Animations will not work.');
        return;
      }

      // Reset animation state for all elements before re-observing
      animatedElements.forEach(element => resetElementAnimation(element));
      staggeredElements.forEach(parent => {
        Array.from(parent.children).forEach(child => resetElementAnimation(child));
      });

      // Create a new AnimationObserver instance for this section
      const animator = new AnimationObserver();

      // Observe basic animated elements
      if (animatedElements.length > 0) {
        animatedElements.forEach(element => {
          animator.observer.observe(element);
        });
      }

      // Observe staggered elements
      if (staggeredElements.length > 0) {
        staggeredElements.forEach(element => {
          // Preserve existing stagger delay or use default
          if (!element.dataset.staggerDelay) {
            element.dataset.staggerDelay = 100;
          }
          animator.staggerObserver.observe(element);
        });
      }

      // Store the animator instance on the section for cleanup
      sectionElement._axiomAnimator = animator;
    }
  } catch (error) {
    console.error('Error initializing section animations:', error);
  }
}

/**
 * Clean up animations for a section being unloaded
 * @param {Element} sectionElement - The section DOM element
 */
function cleanupSectionAnimations(sectionElement) {
  try {
    if (!sectionElement) return;

    // Destroy the observer to prevent memory leaks
    if (sectionElement._axiomAnimator) {
      sectionElement._axiomAnimator.destroy();
      delete sectionElement._axiomAnimator;
    }
  } catch (error) {
    console.error('Error cleaning up section animations:', error);
  }
}

/**
 * Handle section load event
 * Fired when a section is added or re-rendered in theme editor
 */
document.addEventListener('shopify:section:load', (event) => {
  // Get section element from event target
  const sectionElement = event.target;

  // Delay to ensure DOM is fully rendered
  // Using 100ms for better reliability with complex sections
  setTimeout(() => {
    initializeSectionAnimations(sectionElement);
  }, 100);
});

/**
 * Handle section unload event
 * Fired when a section is deleted or about to be re-rendered
 */
document.addEventListener('shopify:section:unload', (event) => {
  // Use event.target directly instead of querying DOM
  // This is more reliable as the element might already be removed
  const sectionElement = event.target;
  cleanupSectionAnimations(sectionElement);
});

/**
 * Handle section reorder event
 * Sections maintain their state, no action needed
 */
document.addEventListener('shopify:section:reorder', () => {
  // Sections are reordered but not re-rendered
  // Animations should already be in their final state
});

/**
 * Handle block select event
 * Optional: Add visual feedback if needed
 */
document.addEventListener('shopify:block:select', (_event) => {
  // Future: Could add visual feedback or trigger specific animations
});

/**
 * Handle block deselect event
 * Optional: Remove visual feedback if added in block:select
 */
document.addEventListener('shopify:block:deselect', (_event) => {
  // Future: Could remove visual feedback
});
